/**
 * Calendar utility functions for date handling and calendar grid generation
 * Using Day.js for date manipulation
 */

import dayjs, { Dayjs } from 'dayjs';

export interface CalendarDay {
  date: Dayjs;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
}

/**
 * Get the number of days in a given month
 */
export function getDaysInMonth(year: number, month: number): number {
  return dayjs().year(year).month(month).daysInMonth();
}

/**
 * Get the first day of the month (0 = Sunday, 6 = Saturday)
 */
export function getFirstDayOfMonth(year: number, month: number): number {
  return dayjs().year(year).month(month).date(1).day();
}

/**
 * Generate calendar grid for a given month
 * Returns array of 35 or 42 days (5 or 6 weeks) including previous/next month days
 */
export function generateCalendarGrid(year: number, month: number): CalendarDay[] {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = dayjs().startOf('day');

  const grid: CalendarDay[] = [];

  // Previous month days
  const prevMonth = dayjs().year(year).month(month).subtract(1, 'month');
  const daysInPrevMonth = prevMonth.daysInMonth();

  for (let i = firstDay - 1; i >= 0; i--) {
    const dayNumber = daysInPrevMonth - i;
    const date = prevMonth.date(dayNumber).startOf('day');

    grid.push({
      date,
      dayNumber,
      isCurrentMonth: false,
      isToday: date.isSame(today, 'day'),
    });
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const date = dayjs().year(year).month(month).date(day).startOf('day');

    grid.push({
      date,
      dayNumber: day,
      isCurrentMonth: true,
      isToday: date.isSame(today, 'day'),
    });
  }

  // Next month days to complete the grid (ensure 35 or 42 days total)
  const remainingDays = 35 - grid.length;
  const totalDays = remainingDays >= 0 ? 35 : 42;
  const nextMonthDays = totalDays - grid.length;

  const nextMonth = dayjs().year(year).month(month).add(1, 'month');

  for (let day = 1; day <= nextMonthDays; day++) {
    const date = nextMonth.date(day).startOf('day');

    grid.push({
      date,
      dayNumber: day,
      isCurrentMonth: false,
      isToday: date.isSame(today, 'day'),
    });
  }

  return grid;
}

/**
 * Format month and year for display (e.g., "June 2025")
 */
export function formatMonthYear(year: number, month: number): string {
  return dayjs().year(year).month(month).format('MMMM YYYY');
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Dayjs, date2: Dayjs): boolean {
  return date1.isSame(date2, 'day');
}

/**
 * Format date for display (e.g., "Mon, Jun 12")
 */
export function formatShortDate(date: Dayjs): string {
  return date.format('ddd, MMM D');
}

/**
 * Get the start and end of the current week (Sunday to Saturday)
 */
export function getCurrentWeekRange(): { start: Dayjs; end: Dayjs } {
  const today = dayjs();
  const dayOfWeek = today.day();

  const start = today.subtract(dayOfWeek, 'day').startOf('day');
  const end = today.add(6 - dayOfWeek, 'day').endOf('day');

  return { start, end };
}
